import ApiSmile from '@/api/api-smile';
import PageData from '@/types/page-data';
import LoadingUtil from '@/util/loading-util';
import MessageUtil from '@/util/message-util';
import React, {useEffect, useState} from 'react';
import PageParam from '@/types/page-param';
import PageContainer from '@/component/page-container';
import PaginationTable, {ColumnType} from '@/component/pagination-table';
import Utils from '@/util/utils';
import RadioFilter from '@/component/radio-filter';
import useDeviceFeature from '@/hooks/use-device-feature';
import {Pagination} from 'antd';
import AppUtil from '@/util/app-util';

const holdFilterOption = [
    {
        label: '最近购买',
        value: 'time_latest',
    },
    {
        label: '收益最高',
        value: 'profit_rate_high',
    },
    {
        label: '收益最低',
        value: 'profit_rate_low',
    },
];

const getColumnData = (): ColumnType<HoldRecent>[] => {
    return [
        {
            title: '代码',
            dataIndex: 'code',
            key: 'code',
        },
        {
            title: '名称',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: '数量',
            dataIndex: 'quantity',
            key: 'quantity',
        },
        {
            title: '买入时间',
            dataIndex: 'buyTime',
            key: 'buyTime',
            render: (text: number) => Utils.formatDate(text),
        },
        {
            title: '买入价格',
            dataIndex: 'buyPrice',
            key: 'buyPrice',
            render: (text: number) => Utils.formatPrice(text),
        },
        {
            title: '现价',
            dataIndex: 'latestPrice',
            key: 'latestPrice',
            render: (text: number, item: HoldRecent) => Utils.formatPrice(item.latestPrice < 0 ? undefined : text),
        },
        {
            title: '收益',
            dataIndex: 'profit',
            key: 'profit',
            render: (text: number, item: HoldRecent) => Utils.formatPrice(item.latestPrice < 0 ? undefined : text),
        },
        {
            title: '收益率',
            dataIndex: 'profitRate',
            key: 'profitRate',
            render: (text: number, item: HoldRecent) => Utils.formatPercentage(item.latestPrice < 0 ? undefined : text),
        },
    ];
};

interface QueryFilter {
    order: string;
}

export interface HoldRecent {
    buyPrice: number;
    buyTime: number;
    code: string;
    createTime: string;
    id: number;
    latestPrice: number;
    modifyTime: string;
    name: string;
    profit: number;
    profitRate: number;
    quantity: number;
}

type ListProps = {
    data?: PageData<HoldRecent>;
    setPageParam?: (page: PageParam) => void;
};

const RecentTable = (props: ListProps) => {
    const {data, setPageParam} = props;
    return <PaginationTable columns={getColumnData()} data={data} setPageParam={setPageParam} />;
};

type RowCol = {
    key: string;
    value: string | number;
    color?: string;
};

const RecentCard = (props: ListProps) => {
    const {data, setPageParam} = props;

    const renderRow = (col1: RowCol, col2: RowCol) => {
        return (
            <div className="flex mt-1">
                <div className="flex-1">
                    <span className="text-sm text-slate-700">{col1.key}</span>
                    <span className={`ml-1 text-sm font-medium ${col1.color || 'text-slate-900'}`}>{col1.value}</span>
                </div>
                <div className="flex-1">
                    <span className="text-sm text-slate-700">{col2.key}</span>
                    <span className={`ml-1 text-sm font-medium ${col2.color || 'text-slate-900'}`}>{col2.value}</span>
                </div>
            </div>
        );
    };

    return (
        <div>
            {data?.content.map((item) => (
                <div key={item.id} className="mb-3 border rounded shadow p-3">
                    <span className="text-base text-slate-900 font-bold">{item.name}</span>
                    <span className="ml-2 text-xs text-slate-500">{item.code}</span>
                    {renderRow({key: '买入时间', value: Utils.formatDate(item.buyTime)}, {key: '数量', value: item.quantity})}
                    {renderRow(
                        {key: '买入价格', value: Utils.formatPrice(item.buyPrice)},
                        {key: '现价', value: Utils.formatPrice(item.latestPrice < 0 ? undefined : item.latestPrice)},
                    )}
                    {renderRow(
                        {
                            key: '收益',
                            value: Utils.formatPrice(item.latestPrice < 0 ? undefined : item.profit),
                            color: item.profit > 0 ? 'text-red-500' : item.profit < 0 ? 'text-green-500' : '',
                        },
                        {
                            key: '收益率',
                            value: Utils.formatPercentage(item.latestPrice < 0 ? undefined : item.profitRate),
                            color: item.profitRate > 0 ? 'text-red-500' : item.profitRate < 0 ? 'text-green-500' : '',
                        },
                    )}
                </div>
            ))}
            {data && (
                <div className="my-6 flex justify-center">
                    <Pagination
                        current={data.page + 1}
                        pageSize={data.size}
                        total={data.total}
                        hideOnSinglePage
                        showSizeChanger
                        onChange={(page, pageSize) => {
                            setPageParam?.({page: page - 1, size: pageSize});
                            AppUtil.scrollToTop();
                        }}
                    />
                </div>
            )}
        </div>
    );
};

const ReportRecent = () => {
    const [data, setData] = useState<PageData<HoldRecent>>();
    const [pageParam, setPageParam] = useState<PageParam>({page: 0, size: 10});
    const [orderState, setOrderState] = useState('time_latest');
    const [queryFilter, setQueryFilter] = useState<QueryFilter>({order: orderState});
    const {isPhone} = useDeviceFeature();

    useEffect(() => {
        const load = async () => {
            LoadingUtil.show();
            const response: any = await ApiSmile.getRecentBuy(pageParam, queryFilter);
            if (response?.data?.code === 0) {
                setData(response.data.data);
            } else {
                const msg = response?.data?.msg || '网络请求失败';
                MessageUtil.error(msg);
            }
            LoadingUtil.hide();
        };

        load();
    }, [pageParam, queryFilter]);

    const renderFilter = () => {
        return (
            <div className="p-4">
                <RadioFilter
                    title="排序："
                    options={holdFilterOption}
                    value={orderState}
                    onChange={(value) => {
                        setOrderState(value);
                        setPageParam({...pageParam, page: 0});
                        setQueryFilter({order: value});
                    }}
                />
            </div>
        );
    };

    return (
        <PageContainer>
            {renderFilter()}
            {isPhone ? <RecentCard data={data} setPageParam={setPageParam} /> : <RecentTable data={data} setPageParam={setPageParam} />}
        </PageContainer>
    );
};

export default ReportRecent;
